import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, ViewEncapsulation } from '@angular/core'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { BookmarkService } from 'src/app/service/data/bookmark.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { BookmarkStateService } from 'src/app/service/state/bookmark-state.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Themes } from '../../enums/enums'
import { CollectionItem } from '../../models/main/collection'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcButtonUOneComponent } from '../ddc-button-u-one/ddc-button-u-one.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'
@Component({
  selector: 'ddc-choose-dialog-one',
  templateUrl: './ddc-choose-dialog-one.component.html',
  styleUrl: './ddc-choose-dialog-one.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    DdcTextGradientComponent,
    DdcButtonSOneComponent,
    DdcButtonUOneComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcChooseDialogOneComponent {
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string
  private themeSubscription: Subscription

  _addedToOptions: boolean = false

  _isEditBtnLoading = false
  _submitBtnText = 'Add to Collection'
  _maxLength: number = 100
  _editError: string = ''
  _editedField: string = ''
  _addedWithoutColl: boolean = false
  _deletedField: boolean = false

  _type: string = ''
  _obj_id: number
  _content_type_id: number
  _content_item: CollectionItem // The content item to add to state
  _options: any[]

  _form = new FormGroup({
    // ! Category
    _option: new FormControl('', [Validators.required]),
  })

  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  constructor(
    private logger: DdcLoggerService,
    public themeService: ThemeService,
    private bookmark_service: BookmarkService,
    private bookmarkStateService: BookmarkStateService,
    public dialog: MatDialog,
    private editDialogRef: MatDialogRef<DdcChooseDialogOneComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (data) {
      this._type = data.type
      this._obj_id = data.obj_id
      this._content_type_id = data.content_type_id
      this._content_item = data.content_item
    }

    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this.getOptions()
  }

  getOptions(): void {
    this.logger.info('DdcChooseDialogOneComponent: getOptions()')
    this.bookmark_service.get_bookmark_groups().subscribe((res: any) => {
      if (res) {
        this._options = res
      }
    })
  }

  add(withColl: boolean): void {
    this.logger.info('DdcChooseDialogOneComponent: add()')
    if (!withColl && this._editedField === 'Add without collection') this.closeDialog()

    if (this._addedWithoutColl || (this._editedField && !this._addedWithoutColl)) this.closeDialog()

    if (withColl) {
      if (!this._form.value._option) return

      this._isEditBtnLoading = true
      this._submitBtnText = 'Adding...'

      if (this._type == 'bookmark-group') {
        const selectedCollectionId = parseInt(this._form.value._option)
        this.bookmark_service
          .bookmark(true, this._content_type_id, this._obj_id, selectedCollectionId)
          .subscribe((res: any) => {
            if (res.success) {
              this._editError = ''
              this._editedField = this._form.value._option
              this._addedToOptions = true

              // Add to bookmark state
              if (this._content_item) {
                this.bookmarkStateService.addBookmarkItem(this._content_item, selectedCollectionId)
              }
            } else {
              this.logger.error(
                'DdcChooseDialogOneComponent: add() | bookmark_service.bookmark (group): res:',
                res,
              )
              this._editError = res.res.error
              this._editedField = ''
            }
            this._isEditBtnLoading = false
            this._submitBtnText = 'Edit'
          })
      }
    } else {
      this._isEditBtnLoading = true
      this.bookmark_service
        .bookmark(true, this._content_type_id, this._obj_id, null)
        .subscribe((res: any) => {
          if (res.success) {
            this._editError = ''
            this._editedField = 'edited'
            this._addedToOptions = true
            this._addedWithoutColl = true
            this._isEditBtnLoading = false

            // Add to bookmark state (all items)
            if (this._content_item) {
              this.bookmarkStateService.addBookmarkItem(this._content_item)
            }
          } else {
            this.logger.error(
              'DdcChooseDialogOneComponent: closeDialog() | bookmark_service.bookmark (group): res:',
              res,
            )
            this._editError = res.res.error
            this._isEditBtnLoading = false
            this._editedField = ''
          }
        })
    }
  }

  closeDialog(): void {
    if (this._addedWithoutColl) {
      this.editDialogRef.close('added')
    }

    if (this._editError != '' && this._editedField == '') {
      const target = 'error'
      this.editDialogRef.close(target)
    }

    let target: String = ''

    if (this._editedField) {
      if (this._addedToOptions) this.editDialogRef.close('added')
      else this.editDialogRef.close('close')
    }

    if (!this._addedToOptions) {
      this.bookmark_service
        .bookmark(true, this._content_type_id, this._obj_id, null)
        .subscribe((res: any) => {
          if (res.success) {
            target = 'added'

            // Add to bookmark state (all items)
            if (this._content_item) {
              this.bookmarkStateService.addBookmarkItem(this._content_item)
            }

            this.editDialogRef.close(target)
          } else {
            this.logger.error(
              'DdcChooseDialogOneComponent: closeDialog() | bookmark_service.bookmark (group): res:',
              res,
            )
            this._editError = res.res.error
            this._editedField = ''
            target = 'error'
            this.editDialogRef.close(target)
          }
        })
    }
  }

  get field(): any {
    return this._form.get('_field')
  }
}
